import { Link, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { LayoutDashboard, Briefcase, Sparkles, FileText, HelpCircle, Calendar, LogOut, StickyNote, Phone } from "lucide-react";

const Navbar = ({ user, onLogout }) => {
  const location = useLocation();

  const isActive = (path) => location.pathname === path;

  const isLegalProfessional = ["lawyer", "jurist", "law_student"].includes(user?.user_type);
  
  const navItems = isLegalProfessional ? [
    { path: "/dashboard", label: "لوحة التحكم", icon: LayoutDashboard },
    { path: "/cases", label: "القضايا", icon: Briefcase },
    { path: "/appointments", label: "المواعيد", icon: Calendar },
    { path: "/notes", label: "الملاحظات", icon: StickyNote },
    { path: "/legal-ai", label: "بحث ذكي", icon: Sparkles },
    { path: "/case-analyzer", label: "محلل القضايا", icon: Sparkles },
    { path: "/contract-generator", label: "منشئ العقود", icon: FileText },
    { path: "/reports", label: "التقارير", icon: LayoutDashboard },
    { path: "/contact", label: "تواصل معنا", icon: Phone },
  ] : [
    { path: "/dashboard", label: "لوحة التحكم", icon: LayoutDashboard },
    { path: "/ask-lawyer", label: "أسأل محامي", icon: HelpCircle },
  ];

  return (
    <nav className="bg-white border-b border-slate-200 sticky top-0 z-50 shadow-sm" data-testid="navbar">
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/dashboard" className="flex items-center gap-3 hover:opacity-80 transition-opacity" data-testid="navbar-logo">
            <img src="/logo.jpeg" alt="Al-Tamkeen Logo" className="w-10 h-10 rounded-lg object-cover" />
            <div>
              <h1 className="text-xl font-bold text-slate-900">التمكين</h1>
              <p className="text-xs text-slate-500">Al-Tamkeen</p>
            </div>
          </Link>

          {/* Nav Links */}
          <div className="hidden lg:flex items-center gap-2">
            {navItems.map((item) => {
              const Icon = item.icon;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all ${
                    isActive(item.path)
                      ? "bg-blue-100 text-blue-700 font-semibold"
                      : "text-slate-600 hover:bg-slate-100"
                  }`}
                  data-testid={`nav-link-${item.path.substring(1)}`}
                >
                  <Icon className="w-4 h-4" />
                  <span>{item.label}</span>
                </Link>
              );
            })}
          </div>

          {/* User Info & Logout */}
          <div className="flex items-center gap-4">
            <div className="text-right">
              <p className="text-sm font-semibold text-slate-900" data-testid="user-name">{user?.full_name}</p>
              <p className="text-xs text-slate-500">
                {user?.user_type === "lawyer" ? "محامي" : user?.user_type === "jurist" ? "حقوقي" : user?.user_type === "law_student" ? "طالب قانون" : "مواطن"}
              </p>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={onLogout}
              className="gap-2"
              data-testid="logout-button"
            >
              <LogOut className="w-4 h-4" />
              تسجيل الخروج
            </Button>
          </div>
        </div>

        {/* Mobile Nav */}
        <div className="lg:hidden flex gap-2 pb-3 overflow-x-auto">
          {navItems.map((item) => {
            const Icon = item.icon;
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`flex items-center gap-2 px-3 py-2 rounded-lg whitespace-nowrap transition-all ${
                  isActive(item.path)
                    ? "bg-blue-100 text-blue-700 font-semibold"
                    : "text-slate-600 bg-slate-50"
                }`}
                data-testid={`mobile-nav-link-${item.path.substring(1)}`}
              >
                <Icon className="w-4 h-4" />
                <span className="text-sm">{item.label}</span>
              </Link>
            );
          })}
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
